import { observer } from "mobx-react-lite";
import {
  Button,
  Container,
  Form,
  Grid,
  Label,
  Message,
  Radio,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "../../../app/stores/store";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import PageHeader from "../../../app/layout/PageHeader";
import SettingsNavBar from "../SettingsNavBar";
import SettingsDropdownMenuMobile from "../SettingsDropdownMenuMobile";
import ApplyARToAll from "./ApplyARToAll";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { ErrorMessage, Formik, FormikErrors } from "formik";
import ValidationErrors from "../../errors/ValidationErrors";
import { SettingsAccountsReceivableFormValues } from "../../../app/models/accountsReceivable";
import { yesNoOptions } from "../../../app/common/options/option";
import MyIconDecimalInput from "../../../app/common/form/MyIconDecimalInput";
import MyHelpButton from "../../../app/common/form/MyHelpButton";

export default observer(function ARSettings() {
  const { t } = useTranslation(["common", "ticketSettings"]);
  const navigate = useNavigate();
  const { accountsReceivableSettingsStore, modalStore } = useStore();
  const {
    accountsReceivableSettings,
    loadAccountsReceivableSettings,
    loadingInitial,
    addAccountsReceivableSettings,
    updateAccountsReceivableSettings,
  } = accountsReceivableSettingsStore;

  const validationSchema = Yup.object({
    isActive: Yup.boolean().required(),
    // defaultMaxCreditLimit: Yup.number(),
    applyToAll: Yup.boolean(),
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [completed, setCompleted] = useState<boolean>(false);
  const accountsReceivableSettingsFormValues = useMemo(() => {
    if (accountsReceivableSettings)
      return { ...accountsReceivableSettings, error: undefined };

    return {
      id: undefined,
      isActive: undefined,
      defaultMaxCreditLimit: undefined,
      applyToAll: undefined,
      showMiddleDisclaimer: undefined,
      error: undefined,
    };
  }, [accountsReceivableSettings]);

  function handleFormSubmit(
    values: SettingsAccountsReceivableFormValues,
    setErrors: (
      errors: FormikErrors<SettingsAccountsReceivableFormValues>
    ) => void,
    setSubmitting: Function
  ) {
    setSubmitting(true);
    setCompleted(true);
    const mutation = values.id
      ? updateAccountsReceivableSettings(values)
      : addAccountsReceivableSettings(values);
    mutation
      .then(async () => await loadAccountsReceivableSettings())
      .catch((err) => setErrors({ error: err }))
      .finally(() => setSubmitting(false));
  }

  function interceptFormSubmit(
    values: SettingsAccountsReceivableFormValues,
    setErrors: (
      errors: FormikErrors<SettingsAccountsReceivableFormValues>
    ) => void,
    setSubmitting: Function
  ) {
    if (values.applyToAll && !accountsReceivableSettingsFormValues.applyToAll) {
      modalStore.openModal(
        <ApplyARToAll
          applyToAll={values.applyToAll ?? false}
          handleSave={() => handleFormSubmit(values, setErrors, setSubmitting)}
        />
      );
    } else {
      handleFormSubmit(values, setErrors, setSubmitting);
    }
  }

  /// Devnote:
  /// This is needed because there's mixed usages of null, undefined, number, and strings with the forms and api.
  /// It'd require changing the interfaces which can be done later.
  function valuesChanged(values: SettingsAccountsReceivableFormValues) {
    const isActive = String(values.isActive) === "true";
    const showMiddleDisclaimer = String(values.showMiddleDisclaimer) === "true";

    let defaultMaxCreditLimit: number | undefined;
    if(String(values.defaultMaxCreditLimit) === '' || values.defaultMaxCreditLimit == null) {
      defaultMaxCreditLimit = undefined;
    } else {
      defaultMaxCreditLimit = Number(values.defaultMaxCreditLimit);
    }

    let settingsDefaultMaxCreditLimit: number | undefined;
    if(accountsReceivableSettingsFormValues.defaultMaxCreditLimit == null) {
      settingsDefaultMaxCreditLimit = undefined;
    } else {
      settingsDefaultMaxCreditLimit = accountsReceivableSettingsFormValues.defaultMaxCreditLimit;
    }

    return isActive !== accountsReceivableSettingsFormValues.isActive ||
    showMiddleDisclaimer !== accountsReceivableSettingsFormValues.showMiddleDisclaimer ||
    defaultMaxCreditLimit !== settingsDefaultMaxCreditLimit;
  }

  if (loadingInitial)
    <LoadingComponent
      content={t("accountsReceivableSettings_Loading", {
        ns: "accountsReceivable",
      })}
    />;

  return (
    <>
      <Grid stackable>
        <Grid.Column width={3} className="mobileHeight">
          <SettingsNavBar />
          <SettingsDropdownMenuMobile />
        </Grid.Column>
        <Grid.Column width={13}>
          <PageHeader
            header={`${t("accountsReceivable.header", {
              ns: "settings",
            })}`}
            type={"h2"}
            divider={true}
            getAlerts={true}
            addTitle={true}
            showHelp
            helpName="settings.inventory.ARsettings"
          />
          <>
            <Container className="page">
              <Formik
                validationSchema={validationSchema}
                enableReinitialize
                initialValues={accountsReceivableSettingsFormValues}
                onSubmit={(values, { setErrors, setSubmitting }) =>
                  interceptFormSubmit(
                    {
                      ...values,
                      isActive: values.isActive?.toString() === "true",
                      applyToAll: values.applyToAll?.toString() === "true",
                      showMiddleDisclaimer:
                        (values.showMiddleDisclaimer ?? false).toString() ===
                        "true",
                    },
                    setErrors,
                    setSubmitting
                  )
                }
              >
                {({
                  handleSubmit,
                  isValid,
                  isSubmitting,
                  dirty,
                  values,
                  resetForm,
                  handleChange,
                  setFieldValue,
                  handleBlur,
                  touched,
                  errors,
                }) => (
                  <Form
                    className="ui form"
                    autoComplete="off"
                    onSubmit={handleSubmit}
                  >
                    {completed && isValid && (
                      <Message positive>
                        {t("settingsupdated", {
                          ns: "common",
                        })}
                      </Message>
                    )}
                    <ErrorMessage
                      name="error"
                      render={() => <ValidationErrors errors={errors} />}
                    />
                    <Grid stackable>
                      <Grid.Row columns={2}>
                        <Grid.Column>
                          <label className="required myLabel">
                            {`${t("accountsReceivable.enableAR", {
                              ns: "settings",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.ARtoggle"
                            position="bottom left"
                            color={"blue"}
                          />
                          <Form.Group inline>
                            {yesNoOptions.map((data, index) => (
                              <Form.Field key={`isActive[${data.value}]`}>
                                <Radio
                                  id={`isActive[${index}]`}
                                  key={`isActive[${data.value}]`}
                                  value={data.value}
                                  name="isActive"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  label={data.text}
                                  checked={
                                    values.isActive?.toString() === data.value
                                  }
                                />
                              </Form.Field>
                            ))}
                          </Form.Group>
                        </Grid.Column>
                        <Grid.Column>
                          <label className="myLabel">
                            {`${t("creditLimitMax", {
                              ns: "accountsReceivable",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.creditLimit"
                            position="bottom left"
                            color={"blue"}
                          />
                          <MyIconDecimalInput
                            icon="dollar"
                            iconPosition="left"
                            placeholder="0.00"
                            name="defaultMaxCreditLimit"
                            maxLength={500}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row columns={2}>
                        {/* <Grid.Column>
                          <label className="myLabel">
                            {`${t("applyToAll", {
                              ns: "accountsReceivable",
                            })}:`}
                          </label>
                          <Form.Group inline>
                            {yesNoOptions.map((data, index) => (
                              <Form.Field key={`applyToAll[${data.value}]`}>
                                <Radio
                                  id={`applyToAll[${index}]`}
                                  key={`applyToAll[${data.value}]`}
                                  value={data.value}
                                  name="applyToAll"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  label={data.text}
                                  checked={
                                    values.applyToAll?.toString() === data.value
                                  }
                                />
                              </Form.Field>
                            ))}
                          </Form.Group>
                        </Grid.Column> */}
                        <Grid.Column>
                          <label className="myLabel">
                            {`${t("showARMiddleDisclaimer", {
                              ns: "accountsReceivable",
                            })}:`}
                          </label>
                          <MyHelpButton
                            name="paymentMethodHelp"
                            help="settings.inventory.ARmiddleDisclaimer"
                            position="bottom left"
                            color={"blue"}
                          />
                          <Form.Group inline>
                            {yesNoOptions.map((data, index) => (
                              <Form.Field
                                key={`showARMiddleDisclaimer[${data.value}]`}
                              >
                                <Radio
                                  id={`showARMiddleDisclaimer[${index}]`}
                                  key={`showARMiddleDisclaimer[${data.value}]`}
                                  value={data.value}
                                  name="showMiddleDisclaimer"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  label={data.text}
                                  checked={
                                    (
                                      values.showMiddleDisclaimer ?? false
                                    ).toString() === data.value
                                  }
                                />
                              </Form.Field>
                            ))}
                            {touched.showMiddleDisclaimer &&
                              errors.showMiddleDisclaimer && (
                                <Label basic color="red">
                                  {errors.showMiddleDisclaimer}
                                </Label>
                              )}
                          </Form.Group>
                        </Grid.Column>
                        <Grid.Column>
                          <label className="myLabel">
                            {`${t("ARDisclaimerButton", {
                              ns: "disclaimerSettings",
                            })}:`}
                          </label>
                          <br />
                          <Button
                            className="save_button"
                            type="button"
                            content={t("AR", { ns: "disclaimerSettings" })}
                            onClick={() => {
                              navigate(
                                `/settings/accountsReceivableDisclaimers`
                              );
                            }}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column>
                          <Button
                            color="green"
                            className="save_button"
                            icon="check"
                            content={t("save", { ns: "common" })}
                            disabled={isSubmitting || !dirty || !isValid || !valuesChanged(values)}
                            loading={isSubmitting}
                            type="submit"
                            data-testid="save"
                          />
                          <Button
                            className="save_button"
                            as={Link}
                            to="/settings"
                            icon="cancel"
                            type="reset"
                            content={t("cancel", { ns: "common" })}
                          />
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Container>
          </>
        </Grid.Column>
      </Grid>
    </>
  );
});
